import Papa from 'papaparse'
import type { CsvSchema } from '$lib/utils/csv-validator.js'

export interface CsvParseResult<T> {
	data: T[]
	errors: Papa.ParseError[]
}

export interface CsvValidationRule<T> {
	field: keyof T
	required?: boolean
	type?: 'string' | 'number' | 'boolean'
}

export interface CsvValidationResult {
	isValid: boolean
	errors: Array<{
		row: number
		field: string
		message: string
	}>
}

export enum CsvDelimiter {
	COMMA = ',',
	SEMICOLON = ';',
	TAB = '\t',
	PIPE = '|',
	COLON = ':',
	AUTO = 'auto'
}

export enum CsvEncoding {
	ISO8859_1 = 'iso-8859-1',
	UTF8 = 'utf-8'
}

export const DEFAULT_CSV_DELIMITER = CsvDelimiter.SEMICOLON

export const DEFAULT_CSV_ENCODING = CsvEncoding.UTF8

export interface CsvParseOptions {
	delimiter?: CsvDelimiter
	encoding?: CsvEncoding
	caseInsensitiveHeaders?: boolean
	headerTransformMode?: 'lowercase' | 'camelCase' | 'none'
}

const readFileText = async (file: File, encoding: CsvEncoding): Promise<string> => {
	if (encoding === CsvEncoding.UTF8) {
		return file.text()
	}

	const buffer = await file.arrayBuffer()

	return new TextDecoder(CsvEncoding.ISO8859_1).decode(buffer)
}

function formatCsvError(err: any, delimiter?: string): string {
	const errorMessage =
		Array.isArray(err) && err.length
			? (err[0].message ?? JSON.stringify(err[0]))
			: (err.message ?? String(err))

	if (errorMessage.includes('Too many fields') || errorMessage.includes('Trailing quote')) {
		return `The file could not be parsed. It looks like the wrong delimiter was used${
			delimiter ? ` ("${delimiter}")` : ''
		}. Try a different one (e.g. comma, semicolon, or tab).`
	}

	return `Failed to parse CSV: ${errorMessage}`
}

/**
 * Parse CSV from file
 */
export async function parseCsvFile<T>(
	file: File,
	options: CsvParseOptions = {}
): Promise<CsvParseResult<T>> {
	const {
		delimiter = DEFAULT_CSV_DELIMITER,
		encoding = DEFAULT_CSV_ENCODING,
		caseInsensitiveHeaders = true,
		headerTransformMode = 'none'
	} = options

	const text = await readFileText(file, encoding)
	const normalized = text.replace(/\r\n/g, '\n').replace(/^\uFEFF/, '')

	const transformHeader = (header: string) => {
		let h = header.trim().replace(/^"|"$/g, '')
		if (caseInsensitiveHeaders) {
			if (headerTransformMode === 'lowercase') h = h.toLowerCase()
			else if (headerTransformMode === 'camelCase') {
				h = h.toLowerCase().replace(/[_\s-]+(\w)/g, (_, c) => c.toUpperCase())
			}
		}
		return h
	}

	try {
		return await new Promise((resolve, reject) => {
			Papa.parse<T>(normalized, {
				header: true,
				delimiter,
				dynamicTyping: true,
				skipEmptyLines: true,
				transformHeader,
				transform: (value) =>
					typeof value === 'string' ? value.trim().replace(/^"|"$/g, '') : value,
				complete: (result) => {
					if (result.errors?.length) {
						reject(result.errors)
					} else if (result.data.length && Object.keys(result?.data?.[0])?.length <= 1) {
						reject(
							new Error(
								`It looks like this CSV has only one column — maybe the wrong delimiter (“${delimiter}”) was used.`
							)
						)
					} else {
						resolve({ data: result.data, errors: [] })
					}
				},
				error: (error: unknown) => reject(error)
			})
		})
	} catch (err: any) {
		throw new Error(formatCsvError(err, delimiter))
	}
}

/**
 * Parse CSV from string
 */
export function parseCsvString<T>(
	csvString: string,
	options: Pick<CsvParseOptions, 'delimiter'> = {}
): CsvParseResult<T> {
	const { delimiter = DEFAULT_CSV_DELIMITER } = options
	const result = Papa.parse<T>(csvString, {
		header: true,
		delimiter,
		dynamicTyping: true,
		skipEmptyLines: true
	})

	return {
		data: result.data,
		errors: result.errors
	}
}

/**
 * Validate CSV data
 */
export function validateCsvData<T>(data: T[], rules: CsvValidationRule<T>[]): CsvValidationResult {
	const errors: CsvValidationResult['errors'] = []

	data.forEach((row, rowIndex) => {
		rules.forEach((rule) => {
			const value = row[rule.field]
			const fieldName = String(rule.field)

			// Check required fields
			if (rule.required && (value === undefined || value === null || value === '')) {
				errors.push({
					row: rowIndex + 1,
					field: fieldName,
					message: `${fieldName} is required`
				})
				return
			}

			// Skip if empty and not required
			if (value === undefined || value === null || value === '') {
				return
			}

			// Type validation
			if (rule.type) {
				let isValid = true
				switch (rule.type) {
					case 'string':
						isValid = typeof value === 'string'
						break
					case 'number':
						isValid = typeof value === 'number' && !isNaN(value)
						break
					case 'boolean':
						isValid = typeof value === 'boolean'
						break
				}

				if (!isValid) {
					errors.push({
						row: rowIndex + 1,
						field: fieldName,
						message: `${fieldName} must be ${rule.type}`
					})
				}
			}
		})
	})

	return {
		isValid: errors.length === 0,
		errors
	}
}

/**
 * Convert data to CSV string
 */
export function dataToCsv<T>(data: T[], options: { delimiter?: CsvDelimiter } = {}): string {
	const { delimiter = DEFAULT_CSV_DELIMITER } = options

	return Papa.unparse(data, { delimiter })
}

export const createTemplateGenerator = <T extends Record<string, unknown>>(
	schema: CsvSchema<T>
) => {
	return (sampleData?: Partial<Record<keyof T, string>>) => {
		const headers = [...schema.required, ...(schema.optional ?? [])]
		const headerRow = headers.map(String).join(',')

		if (!sampleData) {
			return headerRow
		}

		const sampleRow = headers
			.map((header) => sampleData[header] || '')
			.map((value) => `"${value}"`)
			.join(',')

		return `${headerRow}\n${sampleRow}`
	}
}
